// App shell — terminal top nav. Primary working tabs live in the top bar;
// secondary (Methodology, Settings) + the operator's Admin entry (superuser only)
// + the role chip live in the user dropdown. The admin back office (Research,
// Automation, Universes, Invites, Audit) lives in pages/admin/, behind /admin.
// Below ~900px the top nav collapses into a hamburger drawer.
import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { api } from "./api.js";
import { useMe } from "./auth.jsx";
import { useTheme, useValueMode } from "./ui.jsx";
import { useMode } from "./mode.jsx";
import { Icon } from "./components/Icon.jsx";
import { BRAND, Mark } from "./brand.jsx";
import { BaselineProvider, useBaselines } from "./baselines.jsx";
import { CommandPalette } from "./components/CommandPalette.jsx";
import { CrisisProvider, useCrises } from "./crises.jsx";
import { AccountProvider, useAccount, isAutoMode } from "./account.jsx";
import { OperationProvider, useOperation } from "./operation.jsx";
import { DataHealthChip } from "./components/Freshness.jsx";
import { AssistantProvider, AssistantDock } from "./assistant.jsx";
import { useClickOutside } from "./useClickOutside.js";
import ErrorBoundary from "./ErrorBoundary.jsx";
import Login from "./pages/Login.jsx";   // eager: first paint for unauthenticated users

// Route-split the authenticated pages so the initial load ships only the shell +
// Dashboard chunk; the heavier charting pages (Designer, Research, Portfolio)
// load on navigation. Big win on the mobile-remote first paint.
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const Today = lazy(() => import("./pages/Today.jsx"));     // Simple-mode home (Proposal C)
const Portfolio = lazy(() => import("./pages/Portfolio.jsx"));
const Designer = lazy(() => import("./pages/Designer.jsx"));
const Trading = lazy(() => import("./pages/Trading.jsx"));
const Calendar = lazy(() => import("./pages/Calendar.jsx"));
const Engine = lazy(() => import("./pages/Engine.jsx"));   // the transparency decision-engine view
const Settings = lazy(() => import("./pages/Settings.jsx"));
const Methodology = lazy(() => import("./pages/Methodology.jsx"));
const Help = lazy(() => import("./pages/Help.jsx"));
const Admin = lazy(() => import("./pages/admin/Admin.jsx"));

// primary tabs (top bar) · secondary tabs (user dropdown). The top bar is
// everything around the user's real investments: see it (Dashboard), act +
// analyze (Portfolio), execute one trade at a time (Trading), experiment
// (Designer — the one what-if lab). Everything exploratory/researchy lives in
// Research (in the dropdown) — no guesswork on the primary path.
// Two-speed nav (Proposal C). PRO = the full power-user tabs. SIMPLE = the three
// operator tabs (Today / Portfolio / Trade); everything exploratory folds into the user-menu
// "Explore" group so the daily path is unmistakable. "Portfolio" is used in both speeds — it's
// the internationally-recognised term (vs trader slang "Book").
const PRIMARY_PRO = [["/", "Dashboard", true], ["/portfolio", "Portfolio"],
  ["/trading", "Trading"], ["/engine", "Plan"], ["/calendar", "Calendar"], ["/designer", "Designer"]];
const PRIMARY_SIMPLE = [["/", "Today", true], ["/portfolio", "Portfolio"], ["/trading", "Trade"], ["/engine", "Plan"]];
const primaryFor = (mode) => (mode === "simple" ? PRIMARY_SIMPLE : PRIMARY_PRO);
// Everyone gets Methodology (curated) + Settings; Research/Automation live behind /admin.
// In Simple mode, Calendar + Designer move here too (out of the primary path). Admins
// additionally get the Admin entry (Django-superuser-gated, matching the server).
const EXPLORE_SIMPLE = [["/calendar", "Calendar"], ["/designer", "Designer"], ["/methodology", "Methodology"]];
const SECONDARY_PRO = [["/methodology", "Methodology"]];
// Help & guides is reachable from every speed (the per-page Help button also deep-links here).
const COMMON = [["/help", "Help & guides"], ["/settings", "Settings"]];
const ADMIN_LINKS = [["/admin", "Admin"]];
const secondaryFor = (me, mode) => {
  const base = [...(mode === "simple" ? EXPLORE_SIMPLE : SECONDARY_PRO), ...COMMON];
  return me?.is_superuser ? [...ADMIN_LINKS, ...base] : base;
};

const initials = (name, email) =>
  (name || email || "?").split(/[\s.@]+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

function UserMenu({ me }) {
  const [open, setOpen] = useState(false);
  const role = me?.role || "VIEWER";
  const { mode, simple, toggle: toggleMode } = useMode();
  const ref = useClickOutside(() => setOpen(false));
  return (
    <div className="user" ref={ref}>
      <button className="user__btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
        <span className="avatar">{initials(me?.name, me?.email)}</span>
        <span className="user__name" data-bind="me.name">{me?.name || me?.email}</span>
        <span className="user__caret"><Icon name="chevronDown" size={13} /></span>
      </button>
      {open && (
        <div className="menu" onClick={() => setOpen(false)}>
          <div className="menu__id">
            <b>{me?.name || me?.email}</b><span>{me?.email}</span>
            <span className={`role role--${role}`} style={{ marginTop: 8, alignSelf: "flex-start" }}>{role}</span>
          </div>
          <div className="menu__sep" />
          {secondaryFor(me, mode).map(([to, label]) => (
            <NavLink key={to} to={to} className="menu__link">{label}</NavLink>
          ))}
          <div className="menu__sep" />
          {/* Two-speed toggle (Proposal C): Simple = calm operator view, Pro = full app */}
          <button className="menu__link" onClick={toggleMode}>
            {simple ? "Switch to Pro view" : "Switch to Simple view"}
            <span className="menu__hint">{simple ? "full app" : "calmer"}</span>
          </button>
          <div className="menu__sep" />
          <a className="menu__signout" href="/accounts/logout/">Sign out</a>
        </div>
      )}
    </div>
  );
}

function BaselineMenu() {
  const { available, selected, toggle } = useBaselines();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  if (!available.length) return null;
  return (
    <div className="baseline-sel" ref={ref}>
      <button className="iconbtn baseline-sel__btn" onClick={() => setOpen((o) => !o)} title="Overlay benchmarks on every plot" aria-haspopup="menu" aria-expanded={open}>
        Benchmarks{selected.length ? ` · ${selected.length}` : ""}
      </button>
      {open && (
        <div className="menu baseline-sel__menu">
          <div className="menu__id" style={{ paddingBottom: 4 }}><b>Overlay on all plots</b></div>
          {available.map((b) => (
            <button key={b.key} className="baseline-sel__row" onClick={() => toggle(b.key)} title={b.desc || undefined}>
              <span className="baseline-sel__chk">{selected.includes(b.key) ? "✓" : ""}</span>
              <span className="baseline-sel__sw" style={{ background: b.color }} />
              <span>{b.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Account switcher (desktop top-bar) — pick the active broker account. The
// trigger shows the broker + a colour-coded env badge; it turns RED on a live
// (real-money) account so it's unmistakable at a glance.
function AccountMenu() {
  const { accounts, id, select, live, broker, env } = useAccount();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  // N6: a freshly-invited user has no broker connected (accounts empty). Don't vanish the
  // control — offer the one entry point: connect an account (Settings → Brokers).
  if (!accounts.length) {
    return (
      <Link to="/settings#brokers" className="acct-trigger" data-testid="connect-account-cta">
        <span className="acct-trigger__broker">Connect an account</span>
        <span className="acct-trigger__caret"><Icon name="chevronRight" size={11} /></span>
      </Link>
    );
  }
  return (
    <div className="baseline-sel acct-sel" ref={ref}>
      <button className={"acct-trigger" + (live ? " acct-trigger--live" : "")}
        onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}
        title={live ? "LIVE account — real money" : "Demo account — paper money"}>
        <span className={"acct-dot " + (live ? "acct-dot--live" : "acct-dot--demo")} />
        <span className="acct-trigger__broker">{broker}</span>
        <span className={"acct-badge " + (live ? "acct-badge--live" : "acct-badge--demo")}>{live ? "LIVE" : "PAPER"}</span>
        <span className="acct-trigger__caret"><Icon name="chevronDown" size={11} /></span>
      </button>
      {open && (
        <div className="menu acct-menu">
          <div className="menu__id" style={{ paddingBottom: 4 }}><b>Active account</b><span>PAPER = no real money · LIVE = real money</span></div>
          {accounts.map((a) => (
            <button key={a.id} className={"acct-menu__row" + (a.id === id ? " acct-menu__row--on" : "")} onClick={() => { select(a.id); setOpen(false); }}>
              <span className={"acct-dot " + (a.live ? "acct-dot--live" : "acct-dot--demo")} />
              <span className="acct-menu__name">{a.label}</span>
              <span className={"acct-badge " + (a.live ? "acct-badge--live" : "acct-badge--demo")}>{a.live ? "LIVE" : "PAPER"}</span>
              {a.risk_profile && a.risk_profile !== "core" &&
                <span data-testid="acct-risk" style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, border: "1px solid #8886" }}>{a.risk_profile}</span>}
              <span className="acct-menu__chk">{a.id === id ? "✓" : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Inline account switcher for the mobile drawer — selectable rows (no dropdown),
// the clearest way to switch on a phone. Same colour-coded LIVE/DEMO signalling.
function AccountSwitcher() {
  const { accounts, id, select } = useAccount();
  // N6: no accounts yet → offer the connect-an-account entry point instead of vanishing.
  if (!accounts.length) {
    return (
      <div className="acct-switch">
        <div className="drawer-ctl__label">Account</div>
        <div className="acct-switch__opts">
          <Link to="/settings#brokers" className="acct-opt" data-testid="connect-account-cta">
            <span className="acct-opt__name">Connect an account</span>
            <Icon name="chevronRight" size={13} />
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="acct-switch">
      <div className="drawer-ctl__label">Account</div>
      <div className="acct-switch__opts">
        {accounts.map((a) => (
          <button key={a.id} className={"acct-opt" + (a.id === id ? " acct-opt--on" : "") + (a.live ? " acct-opt--live" : "")}
            onClick={() => select(a.id)} aria-pressed={a.id === id}>
            <span className={"acct-dot " + (a.live ? "acct-dot--live" : "acct-dot--demo")} />
            <span className="acct-opt__name">{a.label}</span>
            <span className={"acct-badge " + (a.live ? "acct-badge--live" : "acct-badge--demo")}>{a.live ? "LIVE" : "PAPER"}</span>
            {a.risk_profile && a.risk_profile !== "core" &&
              <span data-testid="acct-risk" style={{ fontSize: 10, padding: "1px 5px", borderRadius: 4, border: "1px solid #8886" }}>{a.risk_profile}</span>}
            {a.id === id && <span className="acct-opt__chk">✓</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// Full-width real-money warning — impossible to miss when on a live account.
// MANUAL is exempt: it's credential-less (Helm can't place an order there), so the
// "placement is gated" warning is meaningless noise for it.
function LiveBanner() {
  const { live, label, broker } = useAccount();
  if (!live || broker === "MANUAL") return null;
  return (
    <div className="live-banner" role="alert">
      ● LIVE ACCOUNT — {label} · real money. Order placement is gated here for safety; record trades manually.
    </div>
  );
}

// Execution-mode CHIP — sits in front of the account selector (was a full-width strip; the owner
// asked for one concise chip). It carries the ONE safety-critical fact in its tooltip: Helm never
// trades on its own — the user places every trade. Mode only changes HOW: Manual = one name at a
// time (Trade); Auto = the whole target in one click (Settings → Deploy). There is NO scheduled
// execution (the daily job is credential-less). Live accounts also carry the red LiveBanner.
// Shared unread-activity state (docs/design/PORTFOLIO_CENTRIC_UI.md) — ONE fetch feeds BOTH the topbar
// bell (desktop) and the Portfolio-tab dot (glanceable on mobile), so marking the feed seen clears both
// at once (no divergence). The durable-completion surface: order fills / recorded trades that landed
// since the user last looked, so a fill isn't lost to a 2.6s toast. Per-account; refetches on account
// switch + window focus (a fill that lands while away shows the moment the user returns). Live push is P2.
const ActivityCtx = createContext({ unread: 0, markSeen: () => {} });
function ActivityProvider({ children }) {
  const { qs, id, ready } = useAccount();
  const { pathname } = useLocation();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    const load = () => api(`account/activity?${qs}`).then((r) => { if (alive) setUnread(r?.unread || 0); }).catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => { alive = false; window.removeEventListener("focus", load); };
  }, [qs, id, ready]);
  const markSeen = () => { setUnread(0); api(`account/activity?${qs}`, { method: "POST" }).catch(() => {}); };
  // Landing on Portfolio IS seeing the feed → clear the dot (whether reached via the bell or the tab).
  useEffect(() => { if (pathname === "/portfolio" && unread > 0) markSeen(); }, [pathname, unread]);   // eslint-disable-line react-hooks/exhaustive-deps
  return <ActivityCtx.Provider value={{ unread, markSeen }}>{children}</ActivityCtx.Provider>;
}
const useActivity = () => useContext(ActivityCtx);

// Small unread dot appended to the Portfolio nav tab — the mobile-glanceable twin of the desktop bell
// (which is hidden on the narrow bar). Zero layout impact when there's nothing new.
function NavUnreadDot() {
  const { unread } = useActivity();
  if (!unread) return null;
  return <span className="nav-dot" aria-label={`${unread} new`} title={`${unread} new in recent activity`} />;
}

function ActivityBell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { unread, markSeen } = useActivity();
  if (pathname.startsWith("/admin")) return null;
  const open = () => { if (unread > 0) markSeen(); navigate("/portfolio#activity"); };
  return (
    <button className="iconbtn bell-trigger" style={{ position: "relative" }} onClick={open}
      aria-label={unread ? `${unread} new in recent activity` : "Recent activity"}
      title="Recent activity — order fills & recorded trades">
      <Icon name="bell" size={16} />
      {unread > 0 && (
        <span aria-hidden="true" style={{ position: "absolute", top: 1, right: 1, minWidth: 14, height: 14,
          padding: "0 3px", borderRadius: 7, background: "var(--accent)", color: "#fff", fontSize: 9,
          fontWeight: 700, lineHeight: "14px", textAlign: "center", boxSizing: "border-box" }}>
          {unread > 9 ? "9+" : unread}</span>)}
    </button>
  );
}

function ExecModeChip() {
  const { mode, ready } = useAccount();
  const { pathname } = useLocation();
  if (!ready || pathname.startsWith("/admin")) return null;
  const auto = isAutoMode(mode);
  return (
    <Link to="/settings#brokers" data-testid="mode-chip"
      className={"mode-chip mode-chip--" + (auto ? "auto" : "manual")}
      title={(auto
        ? "AUTO — apply the whole target in one click (Settings → Deploy)."
        : "MANUAL — place each trade yourself, one at a time.") + " Helm never trades on its own."}>
      {auto ? "AUTO" : "MANUAL"}</Link>
  );
}

function CrisisMenu() {
  const { windows, selected, toggle, setAll } = useCrises();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(() => setOpen(false));
  if (!windows.length) return null;
  const all = selected.length === windows.length;
  return (
    <div className="baseline-sel" ref={ref}>
      <button className="iconbtn baseline-sel__btn" onClick={() => setOpen((o) => !o)} title="Shade historical market-downturn windows on every plot" aria-haspopup="menu" aria-expanded={open}>
        <span className="lbl-long">Market downturns</span><span className="lbl-short">Downturns</span>{selected.length ? ` · ${selected.length}` : ""}
      </button>
      {open && (
        <div className="menu baseline-sel__menu">
          <div className="menu__id" style={{ paddingBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <b>Shade on all plots</b>
            <button className="baseline-sel__all" onClick={() => setAll(!all)}>{all ? "None" : "All"}</button>
          </div>
          {windows.map((w) => (
            <button key={w.key} className="baseline-sel__row" onClick={() => toggle(w.key)}>
              <span className="baseline-sel__chk">{selected.includes(w.key) ? "✓" : ""}</span>
              <span className="baseline-sel__sw" style={{ background: "var(--band-solid)" }} />
              <span>{w.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ValueModeToggle() {
  const { mode, set } = useValueMode();
  return (
    <div className="seg seg--nav" title="Show charts as absolute value or cumulative return">
      <button className={mode === "abs" ? "active" : ""} onClick={() => set("abs")}>$</button>
      <button className={mode === "ret" ? "active" : ""} onClick={() => set("ret")}>%</button>
    </div>
  );
}

function Shell({ children }) {
  const { me } = useMe();
  const { theme, toggle } = useTheme();
  const { mode } = useMode();
  const { busy } = useOperation();   // while an op processes, `inert` the content — blocks mouse AND keyboard/AT (the veil only blocks the pointer)
  const [drawer, setDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => { setDrawer(false); }, [loc.pathname]);   // close drawer on navigate
  // Connected-header elevation: flush at the top, soft shadow + hairline once content scrolls under.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const primary = primaryFor(mode);
  return (
    <>
      <header className={"topnav" + (scrolled ? " topnav--scrolled" : "")}>
        <div className="topnav__inner">
          <button className="hamburger" onClick={() => setDrawer((d) => !d)} aria-label="Menu"><Icon name="menu" size={20} /></button>
          <Link to="/" className="brand" aria-label={`${BRAND.wordmark} — go to ${mode === "simple" ? "Today" : "Dashboard"}`}>
            <span className="brand__mark"><Mark size={16} /></span>
            <span className="brand__word">{BRAND.wordmark}</span>
          </Link>
          <nav className="nav nav--desktop">
            {primary.map(([to, label, end]) => <NavLink key={to} to={to} end={end}>{label}{to === "/portfolio" && <NavUnreadDot />}</NavLink>)}
          </nav>
          <div className="spacer" />
          <div className="topnav__menus">
            <DataHealthChip />
            <ExecModeChip />
            <AccountMenu />
            <BaselineMenu />
            <CrisisMenu />
          </div>
          <ValueModeToggle />
          <ActivityBell />
          {/* FR7: discoverable entry to the ⌘K command palette (also opens on ⌘K / Ctrl+K). */}
          <button className="iconbtn cmdk-trigger" aria-label="Open command palette (⌘K)" title="Command palette — jump to a page or switch account (⌘K)"
            onClick={() => window.dispatchEvent(new Event("cmdk-toggle"))}>⌘K</button>
          <button className="iconbtn" aria-label="Toggle theme" onClick={toggle}><Icon name={theme === "dark" ? "moon" : "sun"} size={16} /></button>
          <div className="divider" />
          <UserMenu me={me} />
        </div>
        {drawer && (
          <nav className="nav-drawer">
            {/* the operator tabs lead (mirrors the desktop top nav); exploratory + account
                links sit under a labelled group so Simple keeps its calm 3-tab identity. */}
            {primary.map(([to, label, end]) => (
              <NavLink key={to} to={to} end={end} onClick={() => setDrawer(false)}>{label}{to === "/portfolio" && <NavUnreadDot />}</NavLink>
            ))}
            <div className="nav-drawer__sep">{mode === "simple" ? "Explore" : "More"}</div>
            {secondaryFor(me, mode).map(([to, label, end]) => (
              <NavLink key={to} to={to} end={end} className="nav-drawer__more-link" onClick={() => setDrawer(false)}>{label}</NavLink>
            ))}
            {/* the global controls that live in the desktop top-bar — surfaced here
                so the account switcher (+ overlays) are reachable on mobile. */}
            <div className="drawer-controls">
              <AccountSwitcher />
              <div className="drawer-controls__row">
                <BaselineMenu />
                <CrisisMenu />
              </div>
            </div>
          </nav>
        )}
      </header>
      <LiveBanner />
      <main className="main" inert={busy ? "" : undefined}>{children}</main>
    </>
  );
}

// Routed content, with the error boundary KEYED by pathname + the selected account so
// (a) navigating away from a crashed page auto-recovers, and (b) switching accounts
// fully REMOUNTS the page — no stale book, hover, or form state carries across from the
// previous account (demo ⊥ live ⊥ IBKR). Lazy chunks are cached, so the remount is free.
function RoutedContent() {
  const { pathname } = useLocation();
  const { simple } = useMode();
  const { id } = useAccount();
  return (
    <ErrorBoundary key={pathname + "|" + (id || "")}>
      <Suspense fallback={<div className="boot">Loading…</div>}>
        <Routes>
          {/* the home route is the one screen that differs by mode: Simple → Today
              (the calm operator cockpit), Pro → Dashboard (the full glance). */}
          <Route path="/" element={simple ? <Today /> : <Dashboard />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/engine" element={<Engine />} />
          <Route path="/designer" element={<Designer />} />
          <Route path="/stocks" element={<Navigate to="/portfolio" replace />} />
          <Route path="/trading" element={<Trading />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/help" element={<Help />} />
          <Route path="/help/:slug" element={<Help />} />
          {/* Research & Automation moved behind /admin (Track A); Admin self-gates
              non-admins to "/". Old paths redirect so existing links don't 404. */}
          <Route path="/admin/*" element={<Admin />} />
          {/* legacy paths redirect straight to their canonical admin section (no
              double-redirect through bare /admin → /admin/overview). */}
          <Route path="/research" element={<Navigate to="/admin/research" replace />} />
          <Route path="/jobs" element={<Navigate to="/admin/automation" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  const { me, loading, outage, retry } = useMe();
  if (loading) return <div className="boot">Loading…</div>;
  // F2: a server/network failure is NOT a logout — say so and offer Retry instead of the Login page.
  if (!me && outage) return (
    <div className="boot" role="alert">
      <p style={{ margin: "0 0 10px" }}>Helm's server is unreachable right now — this is a temporary read failure, <b>not a logout</b>.</p>
      <button className="btn" onClick={retry}>↻ Retry</button>
    </div>
  );
  if (!me) return <Routes><Route path="*" element={<Login />} /></Routes>;
  return (
    <BaselineProvider>
    <CrisisProvider>
    <AccountProvider>
    <AssistantProvider>
    <ActivityProvider>
    <OperationProvider>
    <Shell>
      <RoutedContent />
      <AssistantDock />
      <CommandPalette />
    </Shell>
    </OperationProvider>
    </ActivityProvider>
    </AssistantProvider>
    </AccountProvider>
    </CrisisProvider>
    </BaselineProvider>
  );
}
